import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useI18n } from "../lib/i18n";
import { fmtUSD } from "../lib/format";
import { fmtMonthLabel } from "../lib/format";
import { encodeShare, decodeShare } from "../lib/share";
import { exportSimulationPDF } from "../lib/pdf";
import GrowthChart from "../components/GrowthChart";
import SavedSims from "../components/SavedSims";
import MobileMonthCards from "../components/MobileMonthCards";
import EmbedDialog from "../components/EmbedDialog";
import { REGISTER_URL } from "../lib/config";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { toast, Toaster } from "sonner";
import { AlertTriangle, TrendingUp, Wallet, RefreshCw, Info, Download, Share2, GitCompare, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS_IT = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const MONTHS_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const emptyInput = () => ({
  deposit: "",
  withdrawal_value: "",
  withdrawal_type: "usd",
  withdrawal_mode: "none",
});

function initialState() {
  const params = new URLSearchParams(window.location.search);
  const s = params.get("s");
  if (s) {
    const decoded = decodeShare(s);
    if (decoded) return decoded;
  }
  const arr = Array.from({ length: 120 }, emptyInput);
  arr[0].deposit = "1000";
  return { startYear: 2026, startMonth: 9, inputs: arr };
}

export default function Simulator() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const monthNames = lang === "it" ? MONTHS_IT : MONTHS_EN;

  const seed = useMemo(initialState, []);
  const [startYear, setStartYear] = useState(seed.startYear);
  const [startMonth, setStartMonth] = useState(seed.startMonth);
  const [inputs, setInputs] = useState(seed.inputs);

  const [sim, setSim] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const buildRequest = useCallback(() => ({
    start_year: Number(startYear),
    start_month: Number(startMonth),
    months: 120,
    inputs: inputs
      .map((row, idx) => {
        const dep = parseFloat(row.deposit);
        const wv = parseFloat(row.withdrawal_value);
        const hasDep = !isNaN(dep) && dep > 0;
        const hasW = !isNaN(wv) && wv > 0 && row.withdrawal_mode !== "none";
        if (!hasDep && !hasW && row.withdrawal_mode === "none") return null;
        return {
          month_index: idx,
          deposit: hasDep ? dep : 0,
          withdrawal_value: hasW ? wv : 0,
          withdrawal_type: row.withdrawal_type || "usd",
          withdrawal_mode: row.withdrawal_mode || "none",
        };
      })
      .filter(Boolean),
  }), [startYear, startMonth, inputs]);

  const runSim = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/simulate`, buildRequest());
      setSim(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error");
    } finally {
      setLoading(false);
    }
  }, [buildRequest]);

  useEffect(() => {
    const id = setTimeout(runSim, 300);
    return () => clearTimeout(id);
  }, [runSim]);

  const updateInput = (idx, field, value) => {
    setInputs((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const resetAll = () => {
    setInputs(Array.from({ length: 120 }, emptyInput));
    window.history.replaceState({}, "", window.location.pathname);
  };

  const handleShare = async () => {
    const token = encodeShare({ startYear, startMonth, inputs });
    const url = `${window.location.origin}${window.location.pathname}?s=${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("link_copied"), { description: url.slice(0, 60) + "…" });
    } catch {
      window.prompt(t("link_copied"), url);
    }
    window.history.replaceState({}, "", `?s=${token}`);
  };

  const handleExport = () => {
    if (!sim) return;
    const startLabel = `${monthNames[startMonth - 1]} ${startYear}`;
    exportSimulationPDF({ sim, meta: { startLabel }, lang });
  };

  const summary = sim && {
    deposited: sim.total_deposited,
    withdrawn: sim.total_withdrawn,
    reinvested: sim.total_reinvested,
    capital: sim.final_capital,
    liquidity: sim.final_liquidity,
    total: sim.final_total,
    growth: sim.total_growth,
  };

  const rowsByYear = sim?.rows || [];

  return (
    <div className="wc-container wc-sim" data-testid="simulator-page">
      <Toaster position="top-right" theme="dark" richColors />

      <div className="wc-hero">
        <div className="wc-hero-copy">
          <span className="wc-eyebrow">{lang === "it" ? "Simulatore 10 anni" : "10-year simulator"}</span>
          <h1 className="wc-title" data-testid="sim-title">{t("sim_title")}</h1>
          <p className="wc-lead">{t("sim_subtitle")}</p>
        </div>
        <div className="wc-hero-controls">
          <div className="wc-field">
            <label>{t("year")}</label>
            <Select value={String(startYear)} onValueChange={(v) => setStartYear(Number(v))}>
              <SelectTrigger className="wc-select" data-testid="start-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 8 }, (_, i) => 2025 + i).map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="wc-field">
            <label>{t("month")}</label>
            <Select value={String(startMonth)} onValueChange={(v) => setStartMonth(Number(v))}>
              <SelectTrigger className="wc-select" data-testid="start-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthNames.map((mn, i) => (
                  <SelectItem key={i} value={String(i + 1)}>{mn}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="ghost" onClick={resetAll} className="wc-btn-ghost" data-testid="reset-btn">
            {t("reset")}
          </Button>
        </div>
      </div>

      <div className="wc-actions">
        <SavedSims
          currentState={{ startYear, startMonth, inputs }}
          onLoad={(s) => {
            setStartYear(s.startYear);
            setStartMonth(s.startMonth);
            setInputs(s.inputs);
            window.history.replaceState({}, "", window.location.pathname);
          }}
        />
        <Button onClick={handleShare} className="wc-btn-primary" data-testid="share-btn">
          <Share2 size={14} /> {t("share_link")}
        </Button>
        <Button onClick={handleExport} variant="outline" className="wc-btn-outline" data-testid="export-pdf-btn">
          <Download size={14} /> {t("export_pdf")}
        </Button>
        <Button onClick={() => navigate("/confronta")} variant="outline" className="wc-btn-outline" data-testid="compare-btn">
          <GitCompare size={14} /> {t("nav_compare")}
        </Button>
        <EmbedDialog currentState={{ startYear, startMonth, inputs }} />
      </div>

      {error && <div className="wc-error" data-testid="sim-error">{error}</div>}

      {summary && (
        <div className="wc-kpis" data-testid="sim-summary-top">
          <div className="wc-kpi">
            <span className="wc-kpi-label"><Wallet size={14} /> {t("tot_deposited")}</span>
            <span className="wc-kpi-value" data-testid="kpi-deposited">{fmtUSD(summary.deposited, lang)}</span>
          </div>
          <div className="wc-kpi">
            <span className="wc-kpi-label"><RefreshCw size={14} /> {t("tot_reinvested")}</span>
            <span className="wc-kpi-value">{fmtUSD(summary.reinvested, lang)}</span>
          </div>
          <div className="wc-kpi">
            <span className="wc-kpi-label"><Wallet size={14} /> {t("tot_withdrawn")}</span>
            <span className="wc-kpi-value" data-testid="kpi-withdrawn">{fmtUSD(summary.withdrawn, lang)}</span>
          </div>
          <div className="wc-kpi wc-kpi-strong">
            <span className="wc-kpi-label"><TrendingUp size={14} /> {t("final_total")}</span>
            <span className="wc-kpi-value" data-testid="kpi-final-total">{fmtUSD(summary.total, lang)}</span>
          </div>
        </div>
      )}

      <div className="wc-hint">
        <Info size={16} />
        <span>{t("min_deposit_hint")} · {lang === "it"
          ? "Modalità Continuo: la scelta si ripete finché non la modifichi."
          : "Recurring mode: the choice repeats until you change it."}</span>
      </div>

      <div className="wc-table-wrap wc-hide-mobile" data-testid="sim-table-wrap">
        <table className="wc-table">
          <thead>
            <tr>
              <th className="wc-th-month">#</th>
              <th className="wc-th-month">{t("col_month")}</th>
              <th>{t("col_deposit")}</th>
              <th className="wc-th-num">{t("col_capital_start")}</th>
              <th className="wc-th-num">{t("col_liq_start")}</th>
              <th className="wc-th-num">{t("col_profit")}</th>
              <th className="wc-th-wd">{t("col_withdrawal")}</th>
              <th className="wc-th-num">{t("col_reinvested")}</th>
              <th className="wc-th-num">{t("col_liq_end")}</th>
              <th className="wc-th-num">{t("col_capital_end")}</th>
            </tr>
          </thead>
          <tbody>
            {rowsByYear.map((row, idx) => {
              const rowInput = inputs[idx];
              const yearMark = row.is_year_start;
              return (
                <tr
                  key={idx}
                  className={
                    "wc-tr" +
                    (yearMark ? " wc-tr-year" : "") +
                    (row.warning ? " wc-tr-warn" : "")
                  }
                  data-testid={`sim-row-${idx}`}
                >
                  <td className="wc-td-idx">{idx + 1}</td>
                  <td className="wc-td-month">
                    <span className="wc-month-label">{fmtMonthLabel(row, lang)}</span>
                    {yearMark && idx > 0 && (
                      <span className="wc-year-pill">{row.year}</span>
                    )}
                  </td>
                  <td>
                    <Input
                      type="number"
                      min="0"
                      step="100"
                      placeholder={t("input_deposit_ph")}
                      value={rowInput.deposit}
                      onChange={(e) => updateInput(idx, "deposit", e.target.value)}
                      className="wc-cell-input"
                      data-testid={`deposit-${idx}`}
                    />
                  </td>
                  <td className="wc-td-num">{fmtUSD(row.capital_start, lang)}</td>
                  <td className="wc-td-num wc-td-muted">{fmtUSD(row.liquidity_start, lang)}</td>
                  <td className="wc-td-num wc-td-profit">{fmtUSD(row.month_profit, lang)}</td>
                  <td>
                    <div className="wc-wd-cell">
                      <Input
                        type="number"
                        min="0"
                        placeholder={t("input_wd_ph")}
                        value={rowInput.withdrawal_value}
                        onChange={(e) => updateInput(idx, "withdrawal_value", e.target.value)}
                        className="wc-cell-input wc-cell-input-sm"
                        data-testid={`wd-value-${idx}`}
                      />
                      <Select
                        value={rowInput.withdrawal_type}
                        onValueChange={(v) => updateInput(idx, "withdrawal_type", v)}
                      >
                        <SelectTrigger className="wc-select-mini" data-testid={`wd-type-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="usd">USD</SelectItem>
                          <SelectItem value="pct">%</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select
                        value={rowInput.withdrawal_mode}
                        onValueChange={(v) => updateInput(idx, "withdrawal_mode", v)}
                      >
                        <SelectTrigger className="wc-select-mini" data-testid={`wd-mode-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t("mode_none")}</SelectItem>
                          <SelectItem value="spot">{t("mode_spot")}</SelectItem>
                          <SelectItem value="continuous">{t("mode_continuous")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="wc-wd-actual">
                      {row.withdrawal > 0 && (
                        <span className={row.warning ? "wc-wd-warn" : "wc-wd-ok"}>
                          {row.warning && <AlertTriangle size={12} />}
                          {fmtUSD(row.withdrawal, lang)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="wc-td-num wc-td-accent">{fmtUSD(row.reinvested, lang)}</td>
                  <td className="wc-td-num wc-td-muted">{fmtUSD(row.liquidity_end, lang)}</td>
                  <td className="wc-td-num wc-td-strong">{fmtUSD(row.capital_end, lang)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {sim && (
        <div className="wc-show-mobile">
          <MobileMonthCards rows={rowsByYear} inputs={inputs} updateInput={updateInput} />
        </div>
      )}

      {sim && (
        <section className="wc-chart-section" data-testid="chart-section">
          <div className="wc-chart-header">
            <div>
              <h2 className="wc-h2">{t("chart_title")}</h2>
              <p className="wc-lead" style={{ fontSize: 14, marginTop: -4 }}>{t("chart_subtitle")}</p>
            </div>
          </div>
          <GrowthChart rows={sim.rows} lang={lang} />
        </section>
      )}

      {summary && (
        <div className="wc-summary" data-testid="sim-summary-bottom">
          <div className="wc-summary-grid">
            <div className="wc-summary-cell">
              <span className="wc-summary-label">{t("tot_deposited")}</span>
              <span className="wc-summary-value">{fmtUSD(summary.deposited, lang)}</span>
            </div>
            <div className="wc-summary-cell wc-summary-cell-accent">
              <span className="wc-summary-label">{t("final_capital")}</span>
              <span className="wc-summary-value">{fmtUSD(summary.capital, lang)}</span>
            </div>
            <div className="wc-summary-cell">
              <span className="wc-summary-label">{t("final_liquidity")}</span>
              <span className="wc-summary-value">{fmtUSD(summary.liquidity, lang)}</span>
            </div>
            <div className="wc-summary-cell">
              <span className="wc-summary-label">{t("tot_withdrawn")}</span>
              <span className="wc-summary-value">{fmtUSD(summary.withdrawn, lang)}</span>
            </div>
            <div className="wc-summary-cell wc-summary-cell-strong">
              <span className="wc-summary-label">{t("growth")}</span>
              <span className="wc-summary-value">{fmtUSD(summary.growth, lang)}</span>
            </div>
          </div>
          {loading && <div className="wc-recalc">…</div>}
        </div>
      )}

      {summary && summary.total > summary.deposited && (
        <a
          href={REGISTER_URL}
          target="_blank"
          rel="noreferrer"
          className="wc-cta-banner"
          data-testid="sim-register-cta"
        >
          <div className="wc-cta-copy">
            <span className="wc-eyebrow">
              {lang === "it" ? "Convinto dai numeri?" : "Convinced by the numbers?"}
            </span>
            <h3 className="wc-cta-title">
              {lang === "it"
                ? "Inizia ora con WordCripto"
                : "Get started with WordCripto"}
            </h3>
            <p className="wc-cta-note">
              {lang === "it"
                ? "Iscrizione tramite referral, versamento minimo $500."
                : "Signup via referral, minimum deposit $500."}
            </p>
          </div>
          <div className="wc-cta-btn">
            <UserPlus size={18} />
            <span>{lang === "it" ? "Iscriviti su wordcripto.com" : "Sign up on wordcripto.com"}</span>
          </div>
        </a>
      )}
    </div>
  );
}
